import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionDrawer, { UI, btnSx } from "../components/ActionDrawer";
import { screenKeyFor } from "../components/actionScreens";
import { Empty, PageHead, SectionTitle, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

/* "2h", "3d", "now" */
const ageLabel = (hours) => {
  if (hours === null || hours === undefined) return "";
  if (hours < 1) return "now";
  if (hours < 24) return `${Math.floor(hours)}h`;
  return `${Math.floor(hours / 24)}d`;
};

const STRIPE = {
  blocking: UI.red,
  standard: UI.amber,
  informational: UI.line,
};

const PRIORITY_TAG = {
  blocking: { tone: "b", label: "Blocking" },
  standard: { tone: "s", label: "Standard" },
  informational: { tone: "i", label: "Info" },
};

// Flow 6 — reachable from any on-site technician step. TC1 (job offer)
// already has its own conflict path built into that form; TC4 belongs more
// to the Engagement Officer's inauguration ceremony than an "installation
// incomplete" outcome, so neither gets this button.
const CAN_MARK_INCOMPLETE = new Set(["TC3", "TC5"]);

const ActionQueue = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  const [activeItem, setActiveItem] = useState(null);
  const [dismissingId, setDismissingId] = useState(null);

  const fetchItems = async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const res = await apiService.fetchActionItems(token);
      setItems(res.data || []);
      setMeta(res.meta || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching your action items");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDismiss = async (item) => {
    setDismissingId(item.key || item.id);
    try {
      await apiService.dismissActionItem(item.id, item.orderId, token);
      setItems((prev) => prev.filter((i) => i !== item));
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Could not dismiss this item",
      );
    } finally {
      setDismissingId(null);
    }
  };

  const kpis = meta
    ? [
        { label: "Needs your action", value: meta.needsAction },
        { label: "Blocking", value: meta.blocking, alarm: true },
        { label: meta.thirdLabel || "Open orders", value: meta.thirdValue },
        { label: meta.fourthLabel || "In pipeline", value: meta.fourthValue },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      {/* Falls back to the nav label, never the section title below it. */}
      <PageHead title={meta?.title || "My queue"} lede={meta?.scope} kpis={kpis} />

      <SectionTitle sub="Sorted by how long the item has been open, not by order number. Blocking items hold the order.">
        Needs your action
      </SectionTitle>

      {loadFailed ? (
        <Empty
          title="Could not load your action items"
          sub={
            <Box
              component="button"
              type="button"
              onClick={fetchItems}
              sx={{ ...btnSx("ghost"), margin: "12px auto 0", display: "block" }}
            >
              Retry
            </Box>
          }
        />
      ) : items.length === 0 && !isLoading ? (
        <Empty
          title="Nothing waiting on you"
          sub="New items appear the moment an order needs your decision."
        />
      ) : (
        <Box
          sx={{
            bgcolor: UI.surface,
            border: "1px solid",
            borderColor: UI.line,
            borderRadius: "8px",
            overflow: "hidden",
            marginBottom: "28px",
          }}
        >
          {items.map((item, i) => {
            const priority = item.priority || "standard";
            const tag = PRIORITY_TAG[priority] || PRIORITY_TAG.standard;
            const screenKey = screenKeyFor(item);
            const rowKey = item.key || `${item.id}-${item.orderId || i}`;
            const isDismissing = dismissingId === (item.key || item.id);

            return (
              <Box
                key={rowKey}
                sx={{
                  display: { xs: "block", sm: "grid" },
                  gridTemplateColumns: { sm: "5px 1fr auto" },
                  borderLeft: { xs: "4px solid", sm: "none" },
                  borderLeftColor: STRIPE[priority] || UI.line,
                  borderBottom: i === items.length - 1 ? 0 : "1px solid",
                  borderBottomColor: UI.line,
                }}
              >
                <Box sx={{ display: { xs: "none", sm: "block" }, bgcolor: STRIPE[priority] || UI.line }} />

                <Box sx={{ padding: { xs: "14px 16px 10px", sm: "14px 18px" }, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      flexWrap: "wrap",
                      marginBottom: "5px",
                    }}
                  >
                    <Box sx={{ fontSize: 15, fontWeight: 500, color: UI.ink }}>
                      {item.title}
                    </Box>
                    <Tag tone={tag.tone}>{tag.label}</Tag>
                    {item.stage ? <Tag tone="stage">{item.stage}</Tag> : null}
                    {item.id ? (
                      <Box
                        component="span"
                        sx={{
                          display: { xs: "none", sm: "inline-block" },
                          fontFamily: UI.mono,
                          fontSize: 11,
                          color: UI.ink4,
                          bgcolor: UI.bg2,
                          border: "1px solid",
                          borderColor: UI.line,
                          padding: "1px 5px",
                          borderRadius: "3px",
                        }}
                      >
                        {item.id}
                      </Box>
                    ) : null}
                  </Box>
                  <Box sx={{ fontSize: 13, color: UI.ink3 }}>
                    {item.orderId ? (
                      <Box
                        component="a"
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/dashboard/orders/${item.orderId}`);
                        }}
                        sx={{ color: UI.p, textDecoration: "none" }}
                      >
                        {item.orderNumber}
                      </Box>
                    ) : null}
                    {item.orderNumber && item.meta ? " · " : ""}
                    {item.meta}
                    {item.meta ? " · " : ""}
                    <Box
                      component="span"
                      sx={{
                        color: item.late ? UI.red : UI.ink3,
                        fontWeight: item.late ? 500 : 400,
                      }}
                    >
                      {ageLabel(item.ageHours)}
                    </Box>
                  </Box>
                </Box>

                <Box
                  sx={{
                    padding: { xs: "6px 16px 16px", sm: "14px 18px" },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "stretch", sm: "center" },
                    gap: "8px",
                    whiteSpace: { sm: "nowrap" },
                  }}
                >
                  {screenKey ? (
                    <>
                      <Box
                        component="button"
                        type="button"
                        onClick={() => setActiveItem(item)}
                        sx={{
                          ...btnSx("ghost"),
                          minHeight: { xs: "44px", sm: "auto" },
                          fontSize: { xs: 14, sm: 13 },
                        }}
                      >
                        {item.actionLabel || "Open"}
                      </Box>
                      {CAN_MARK_INCOMPLETE.has(item.id) ? (
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setActiveItem({ ...item, screenKey: "incomplete" })}
                          sx={{ ...btnSx("flat"), minHeight: { xs: "40px", sm: "auto" } }}
                        >
                          Report a problem
                        </Box>
                      ) : null}
                    </>
                  ) : (
                    <Box
                      component="button"
                      type="button"
                      onClick={() => handleDismiss(item)}
                      disabled={isDismissing}
                      sx={{
                        ...btnSx("ghost"),
                        minHeight: { xs: "44px", sm: "auto" },
                        fontSize: { xs: 14, sm: 13 },
                      }}
                    >
                      {isDismissing ? "Dismissing" : item.actionLabel || "Dismiss"}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      <ActionDrawer
        open={Boolean(activeItem)}
        screenKey={screenKeyFor(activeItem)}
        item={activeItem}
        onClose={() => setActiveItem(null)}
        onSaved={() => {
          setActiveItem(null);
          fetchItems();
        }}
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default ActionQueue;
